export interface Point {
  x: number;
  y: number;
}

export interface Rect extends Point {
  width: number;
  height: number;
}

export interface RectSnapResult {
  offset: Point;
  vertical?: number;
  horizontal?: number;
}

export type Matrix = readonly [number, number, number, number, number, number];

export const IDENTITY_MATRIX: Matrix = [1, 0, 0, 1, 0, 0];

export function normalizeRect(rect: Rect): Rect {
  const x = rect.width < 0 ? rect.x + rect.width : rect.x;
  const y = rect.height < 0 ? rect.y + rect.height : rect.y;
  return {
    x: Math.round(x),
    y: Math.round(y),
    width: Math.round(Math.abs(rect.width)),
    height: Math.round(Math.abs(rect.height)),
  };
}

export function intersectRect(a: Rect, b: Rect): Rect | null {
  const x = Math.max(a.x, b.x);
  const y = Math.max(a.y, b.y);
  const right = Math.min(a.x + a.width, b.x + b.width);
  const bottom = Math.min(a.y + a.height, b.y + b.height);
  return right > x && bottom > y ? { x, y, width: right - x, height: bottom - y } : null;
}

/** Finds the smallest screen-tolerant translation that aligns a visible bound edge or center. */
export function snapRectTranslation(rect: Rect, verticalTargets: readonly number[], horizontalTargets: readonly number[], tolerance: number): RectSnapResult {
  const nearest = (values: readonly number[], targets: readonly number[]): { offset: number; target?: number } => {
    let offset = 0;
    let target: number | undefined;
    let distance = tolerance + Number.EPSILON;
    for (const value of values) for (const candidate of targets) {
      const next = candidate - value;
      if (Math.abs(next) < distance) {
        offset = next;
        target = candidate;
        distance = Math.abs(next);
      }
    }
    return distance <= tolerance && target !== undefined ? { offset, target } : { offset: 0 };
  };
  const x = nearest([rect.x, rect.x + rect.width / 2, rect.x + rect.width], verticalTargets);
  const y = nearest([rect.y, rect.y + rect.height / 2, rect.y + rect.height], horizontalTargets);
  return {
    offset: { x: x.offset, y: y.offset },
    ...(x.target === undefined ? {} : { vertical: x.target }),
    ...(y.target === undefined ? {} : { horizontal: y.target }),
  };
}

export function subtractRect(source: Rect, cutter: Rect): Rect[] {
  const intersection = intersectRect(source, cutter);
  if (!intersection) return [source];
  const result: Rect[] = [];
  const sourceRight = source.x + source.width;
  const sourceBottom = source.y + source.height;
  const cutRight = intersection.x + intersection.width;
  const cutBottom = intersection.y + intersection.height;
  if (intersection.y > source.y) {
    result.push({ x: source.x, y: source.y, width: source.width, height: intersection.y - source.y });
  }
  if (cutBottom < sourceBottom) {
    result.push({ x: source.x, y: cutBottom, width: source.width, height: sourceBottom - cutBottom });
  }
  if (intersection.x > source.x) {
    result.push({ x: source.x, y: intersection.y, width: intersection.x - source.x, height: intersection.height });
  }
  if (cutRight < sourceRight) {
    result.push({ x: cutRight, y: intersection.y, width: sourceRight - cutRight, height: intersection.height });
  }
  return result.filter((rect) => rect.width > 0 && rect.height > 0);
}

export function transformPoint(matrix: Matrix, point: Point): Point {
  return {
    x: matrix[0] * point.x + matrix[2] * point.y + matrix[4],
    y: matrix[1] * point.x + matrix[3] * point.y + matrix[5],
  };
}

export function transformRectBounds(matrix: Matrix, rect: Rect): Rect {
  const bounds = transformRectBoundsPrecise(matrix, rect);
  return normalizeRect(bounds);
}

export function transformRectBoundsPrecise(matrix: Matrix, rect: Rect): Rect {
  const corners = [
    transformPoint(matrix, { x: rect.x, y: rect.y }),
    transformPoint(matrix, { x: rect.x + rect.width, y: rect.y }),
    transformPoint(matrix, { x: rect.x, y: rect.y + rect.height }),
    transformPoint(matrix, { x: rect.x + rect.width, y: rect.y + rect.height }),
  ];
  const xs = corners.map((point) => point.x);
  const ys = corners.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}

export function multiplyMatrix(left: Matrix, right: Matrix): Matrix {
  return [
    left[0] * right[0] + left[2] * right[1],
    left[1] * right[0] + left[3] * right[1],
    left[0] * right[2] + left[2] * right[3],
    left[1] * right[2] + left[3] * right[3],
    left[0] * right[4] + left[2] * right[5] + left[4],
    left[1] * right[4] + left[3] * right[5] + left[5],
  ];
}

export function invertMatrix(matrix: Matrix): Matrix | null {
  const determinant = matrix[0] * matrix[3] - matrix[1] * matrix[2];
  if (Math.abs(determinant) < 1e-10) return null;
  return [
    matrix[3] / determinant,
    -matrix[1] / determinant,
    -matrix[2] / determinant,
    matrix[0] / determinant,
    (matrix[2] * matrix[5] - matrix[3] * matrix[4]) / determinant,
    (matrix[1] * matrix[4] - matrix[0] * matrix[5]) / determinant,
  ];
}

export function matrixAround(point: Point, operation: Matrix): Matrix {
  return multiplyMatrix(
    [1, 0, 0, 1, point.x, point.y],
    multiplyMatrix(operation, [1, 0, 0, 1, -point.x, -point.y]),
  );
}
